import { Injectable } from '@angular/core';
import { LL } from 'i18n/i18n.service';
import { AuthService } from 'services/auth.service';
import { CouchLoginReason } from 'services/couch-login-storm';
import { CloudFunctionHttpError } from 'services/cloud-functions.service';
import { isRateLimitedError } from 'services/cloud-functions-errors';
import { AlertController, AlertButtonRole } from 'providers/alert-controller';
import { sleep } from '@cf-shared/shared-util';
import { AuthRole } from '@cf-shared/couch-auth';
import { LoadingController } from 'providers/loading-controller';
import { getMyApp } from 'app/app-interface';
import { LoggingService } from 'services/logging.service';

/**
 * The call that redeems an access code. It answers the error text to show, and an
 * empty string when the code was redeemed. Each page wraps its own server call in
 * one, because the Redeem Code page and the Subscribe page call different
 * functions whose results have different shapes (#6984).
 */
export type AccessCodeRedeemCall = () => Promise<string>;

/**
 * Everything that happens around redeeming an access code, shared by the Redeem
 * Code page and the Subscribe page's code field (#6984): the loading overlay, the
 * couch re-login that picks up the new role, the role check, the handling of a
 * thrown call, and on success the confirmation alert and the move to the app's
 * default page. A refusal is NOT shown here: each page shows it its own way.
 */
@Injectable({ providedIn: 'root' })
export class AccessCodeActivationService {
  constructor(
    private auth: AuthService,
    private loadingCtrl: LoadingController,
    private alertCtrl: AlertController,
    private logging: LoggingService,
  ) {}

  /** Runs the redemption. Answers the error text to show, or an empty string after a success, which has already been shown. */
  async activateAsync(redeem: AccessCodeRedeemCall): Promise<string> {
    let loading = this.loadingCtrl.create({ content: LL.redeemCode.loading.redeemingCode() });
    await loading.present();
    // this prevents menu items from re-querying and therefore wrongly caching the auth
    await getMyApp().markAppLoadingAsync(true);

    let error: string;
    try {
      error = await redeem();

      if (!error) {
        // wait a bit to give time to UI to show the loadingCtrl, so that the refresh doesn't look weird
        let [loginSuccess] = await Promise.all([this.auth.attemptCouchLoginAsync(CouchLoginReason.RedeemCode), sleep(1000)]);

        if (!loginSuccess) {
          error = LL.redeemCode.errors.loginFailed();
        } else if (this.auth.getAuthRole() < AuthRole.Paid) {
          error = LL.redeemCode.errors.activationFailed();
        }
      }
    } catch (err) {
      // #4283: the redeem call can THROW instead of always returning an error —
      // a version-affinity refusal (this server is older than the account's
      // data) surfaces as an HTTP 409, and any other unexpected server failure
      // can throw too. Unexpected conditions must surface to the user
      // (CLAUDE.md), never leave the loading spinner stuck forever, so this is
      // caught here and turned into the same kind of message a refusal shows.
      if (err instanceof CloudFunctionHttpError && err.httpStatus === 409) {
        // this specific, known, recoverable condition (this server is behind
        // this account's data version) already has its own honest message —
        // deliberately not sent to Sentry, matching the choice already made
        // for the same condition in auth.service.ts / online-state.service.ts,
        // where a later couch-login attempt is what reports it to Mixpanel
        // (couch_login_failure / ServerDataVersionMismatch). This code path
        // does NOT itself trigger a couch-login attempt, so a console.warn is
        // the only trace this specific activation-time occurrence leaves.
        console.warn('AccessCodeActivation: activation failed — this server is behind this account\'s data version', err);
        error = LL.app.serverBehindAccountVersion();
      } else if (isRateLimitedError(err)) {
        // too many codes tried in the last hour (#7027): an expected refusal with its own
        // message, and the server already reported it, so nothing goes to Sentry here
        error = LL.common.errors.tooManyAttempts();
      } else {
        // anything else here is a genuinely unexpected failure (a real server
        // error, a network failure that escaped the cloud-functions retry
        // wrapper, …) — must surface to Sentry, not just the console, or a
        // future debugger has no record of why activation failed.
        this.logging.trackAbnormalException(err);
        error = LL.redeemCode.errors.activationFailed();
      }
    }

    await loading.dismiss();
    await getMyApp().markAppLoadingAsync(false);

    if (error) {
      return error;
    }

    await this.alertCtrl.showAlertAsync({
      message: LL.redeemCode.alerts.successfullyRedeemed(),
      enableBackdropDismiss: true,
      buttons: [
        {
          text: LL.common.buttons.ok(),
          role: AlertButtonRole.Primary,
        },
      ],
    });

    // redirect the app where needed and hide the loading screen
    await getMyApp().gotoDefaultPageAsync();
    return '';
  }
}
